import { useEffect } from 'react';
import type { BrandConfig } from '../contexts/BrandContext';

export interface DocumentMeta {
  title: string;
  description?: string;
  keywords?: string;
  ogTitle?: string;
  ogDescription?: string;
  ogImage?: string;
  favicon?: string;
}

export function toAbsoluteUrl(url: string): string {
  if (!url) {
    return '';
  }

  if (/^https?:\/\//i.test(url)) {
    return url;
  }

  const path = url.startsWith('/') ? url : `/${url}`;

  if (typeof window !== 'undefined') {
    return `${window.location.origin}${path}`;
  }

  return path;
}

function setMetaTag(
  attribute: 'name' | 'property',
  key: string,
  content: string
): void {
  let element = document.querySelector(
    `meta[${attribute}="${key}"]`
  ) as HTMLMetaElement | null;

  if (!element) {
    element = document.createElement('meta');
    element.setAttribute(attribute, key);
    document.head.appendChild(element);
  }

  element.content = content;
}

function setLinkTag(rel: string, href: string): void {
  let element = document.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null;

  if (!element) {
    element = document.createElement('link');
    element.rel = rel;
    document.head.appendChild(element);
  }

  element.href = href;
}

export function documentMetaFromBrand(brand: BrandConfig): DocumentMeta {
  const config = brand.documentMeta;
  const title = (config?.title ?? brand.name).trim();
  const description = config?.description?.trim();

  return {
    title,
    description,
    keywords: config?.keywords?.trim(),
    ogTitle: (config?.ogTitle ?? title).trim(),
    ogDescription: (config?.ogDescription ?? description)?.trim(),
    ogImage: toAbsoluteUrl(config?.ogImage ?? brand.logoSquare ?? ''),
    favicon: brand.logoSquare
      ? toAbsoluteUrl(brand.logoSquare)
      : `data:image/svg+xml,${encodeURIComponent(
          `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 64 64"><rect width="64" height="64" rx="12" fill="${brand.colors.primary}"/><text x="32" y="50" text-anchor="middle" fill="#ffffff" font-family="Inter,sans-serif" font-size="50" font-weight="700">${brand.name.trim().charAt(0).toUpperCase()}</text></svg>`
        )}`,
  };
}

export function useDocumentMeta(meta: DocumentMeta | null): void {
  useEffect(() => {
    if (!meta) {
      return;
    }

    document.title = meta.title;
    const ogTitle = (meta.ogTitle ?? meta.title).trim();
    setMetaTag('property', 'og:title', ogTitle);
    setMetaTag('property', 'og:type', 'website');

    if (meta.description?.trim()) {
      setMetaTag('name', 'description', meta.description.trim());
    }

    const ogDescription = (meta.ogDescription ?? meta.description)?.trim();
    if (ogDescription) {
      setMetaTag('property', 'og:description', ogDescription);
    }

    if (meta.keywords?.trim()) {
      setMetaTag('name', 'keywords', meta.keywords.trim());
    }

    if (meta.ogImage) {
      setMetaTag('property', 'og:image', toAbsoluteUrl(meta.ogImage));
    }

    if (meta.favicon) {
      setLinkTag('icon', meta.favicon);
    }

    if (typeof window !== 'undefined') {
      setMetaTag('property', 'og:url', window.location.href);
    }
  }, [meta]);
}
